import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { AlertMessageService } from '@shared/services/alert-message.service';
import { firstValueFrom, skipWhile, timeout } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { CurrentUserService } from '../services/current-user.service';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const AccessProfileGuard: CanActivateFn = async (_route: ActivatedRouteSnapshot) => {
  const currentUserService = inject(CurrentUserService);
  const router = inject(Router);
  const authService = inject(AuthService);
  const alertMessageService = inject(AlertMessageService);

  const AUTH_TIMEOUT = 10000; // 10 segundos de timeout

  try {
    // Si estamos en medio del callback de MSAL (URL contiene #code o #error), esperar brevemente a que MSAL termine
    if (window.location.hash.includes('code=') || window.location.hash.includes('error=')) {
      // Esperar mientras MSAL esté procesando (máximo 3 segundos)
      const maxWait = 3000;
      const startTime = Date.now();
      while (authService.isRedirecting) {
        if (Date.now() - startTime > maxWait) {
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      // Esperar un poco más para que LOGIN_SUCCESS complete la navegación (máximo 2 segundos adicionales)
      const maxWaitForNavigation = 2000;
      const navStartTime = Date.now();
      while (window.location.hash.includes('code=') || window.location.hash.includes('error=')) {
        if (Date.now() - navStartTime > maxWaitForNavigation) {
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Permitir que la navegación de LOGIN_SUCCESS tome control
      return true;
    }

    let isLoggedIn = false;

    // Espera si MSAL está procesando el redirect
    if (authService.isRedirecting) {
      await new Promise(resolve => setTimeout(resolve, 500)); // espera breve
    }

    const authType = localStorage.getItem('auth_type');

    if (authType === 'jwt') {
      // Usuario JWT externo - el token está en una cookie HttpOnly
      // El backend verificará la cookie automáticamente
      isLoggedIn = true;
    } else {
      // Para usuarios Azure AD (o sin auth_type definido, asumimos Azure AD por defecto)
      // Sincroniza el estado de login con MSAL
      await authService.initializeMsal();

      // Verifica si ya hay una cuenta activa en MSAL
      await authService.checkAccount();
      if (authService.isLoggedIn) {
        isLoggedIn = true;
      } else {
        try {
          // Espera el stream solo si no hay sesión activa
          isLoggedIn = await firstValueFrom(
            authService.isLoggedInStream.pipe(
              skipWhile(value => value === null || value === undefined),
              timeout(AUTH_TIMEOUT),
            ),
          );
        } catch {
          // Timeout - no hay sesión
          isLoggedIn = false;
        }
      }
    }

    if (isLoggedIn) {
      const currentUser = await currentUserService.get();
      if (currentUser?.user?.userGuid) {
        return true; // Usuario autenticado, permite acceso
      }
    }

    // Si no está loggeado y NO es JWT, iniciar login de Azure AD preservando la URL
    if (authType !== 'jwt') {
      // Solo iniciar login si NO estamos ya en proceso de redirect
      const isRedirecting = localStorage.getItem('azure_login_redirecting');
      if (!isRedirecting) {
        // Usar window.location.pathname que tiene la URL completa que el usuario intentó acceder
        const targetUrl = window.location.pathname + window.location.search;
        localStorage.setItem('azure_login_redirecting', 'true');
        authService.login(targetUrl);
      }
      return false;
    }

    // Para usuarios JWT sin autenticar o sin permisos, redirigir a 403
    await router.navigate(['error/403']);
    return false;
  } catch (error) {
    alertMessageService.showError({ title: 'AccessProfileGuard Error', error: error });

    const authType = localStorage.getItem('auth_type');

    // Si hay error y es usuario Azure AD, iniciar login
    if (authType !== 'jwt') {
      const isRedirecting = localStorage.getItem('azure_login_redirecting');
      if (!isRedirecting) {
        const targetUrl = window.location.pathname + window.location.search;
        localStorage.setItem('azure_login_redirecting', 'true');
        authService.login(targetUrl);
      }
      return false;
    }

    await router.navigate(['error/403']);
    return false;
  }
};
